import { useEffect, useState } from 'react';
import { Clock, AlertTriangle, ListChecks } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Kpis = {
  avg_phase0_days: number | null;
  avg_phase1_days: number | null;
  avg_phase2_days: number | null;
  avg_phase3_days: number | null;
  cowork_pending: number;
  active_delayed: number;
};

/** Dashboard üstünde özet KPI satırı.
 *  - 4 fazın ortalama süresi (son 30 gün teslim edilen siparişlerden)
 *  - Cowork queue'da bekleyen görev sayısı
 *  - Aktif (teslim/iptal olmayan) ve geç olan sipariş sayısı
 */
export default function KpiBar() {
  const [kpis, setKpis] = useState<Kpis | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString();
      const [phases, pending, delayed] = await Promise.all([
        supabase
          .from('v_order_phases')
          .select('phase0_days, phase1_days, phase2_days, phase3_days')
          .gte('delivered_at', since)
          .not('delivered_at', 'is', null),
        supabase
          .from('cowork_tasks')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase
          .from('orders')
          .select('seller_order_id', { count: 'exact', head: true })
          .eq('is_delayed', true)
          .not('stage', 'in', '(teslim,iptal)'),
      ]);

      if (cancelled) return;

      const rows = (phases.data ?? []) as Array<{
        phase0_days: number | null;
        phase1_days: number | null;
        phase2_days: number | null;
        phase3_days: number | null;
      }>;
      const avg = (key: keyof typeof rows[number]): number | null => {
        const vals = rows.map((r) => r[key]).filter((v): v is number => typeof v === 'number');
        return vals.length === 0 ? null : vals.reduce((a, b) => a + b, 0) / vals.length;
      };

      setKpis({
        avg_phase0_days: avg('phase0_days'),
        avg_phase1_days: avg('phase1_days'),
        avg_phase2_days: avg('phase2_days'),
        avg_phase3_days: avg('phase3_days'),
        cowork_pending: pending.count ?? 0,
        active_delayed: delayed.count ?? 0,
      });
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (!kpis) {
    return <div className="text-xs text-slate-400">KPI yükleniyor…</div>;
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      <KpiCard
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Aşama 1: AMZ → Depo"
        value={fmtDays(kpis.avg_phase1_days)}
        sublabel="ort. (son 30g teslim)"
      />
      <KpiCard
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Aşama 2: Depo işleme"
        value={fmtDays(kpis.avg_phase2_days)}
        sublabel="ort. (son 30g teslim)"
      />
      <KpiCard
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Aşama 3: Müşteri kargo"
        value={fmtDays(kpis.avg_phase3_days)}
        sublabel="ort. (son 30g teslim)"
      />
      <KpiCard
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Toplam tedarik"
        value={fmtDays(
          (kpis.avg_phase1_days ?? 0) + (kpis.avg_phase2_days ?? 0) + (kpis.avg_phase3_days ?? 0) || null,
        )}
        sublabel="ort. lead time"
      />
      <KpiCard
        icon={<ListChecks className="w-3.5 h-3.5" />}
        label="Cowork bekliyor"
        value={String(kpis.cowork_pending)}
        sublabel="pending görev"
        warn={kpis.cowork_pending > 50}
      />
      <KpiCard
        icon={<AlertTriangle className="w-3.5 h-3.5" />}
        label="Geciken aktif"
        value={String(kpis.active_delayed)}
        sublabel="is_delayed=true"
        warn={kpis.active_delayed > 0}
      />
    </div>
  );
}

function fmtDays(v: number | null): string {
  if (v === null || Number.isNaN(v)) return '—';
  return v.toFixed(1) + ' g';
}

function KpiCard({ icon, label, value, sublabel, warn }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sublabel?: string;
  warn?: boolean;
}) {
  return (
    <div className={`bg-white rounded-lg border p-2.5 ${warn ? 'border-red-300 ring-1 ring-red-100' : 'border-slate-200'}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-lg font-semibold text-slate-900">{value}</div>
      {sublabel && <div className="text-[10px] text-slate-400 mt-0.5">{sublabel}</div>}
    </div>
  );
}
